/**
 * @descripton Renders the form that allows the user to use the uploaded file to define the mapping between the excel's headers and SECCON's fields.
 */

import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import ErrorBoundary from '@seccon/shared/ui/components/ErrorBoundary';

import FormFooter from './FormFooter';
import MappingField from './MappingField';
import NameField from './NameField';
import SsnSyncField from './SsnSyncField';
import FieldListModal from './FieldListModal';
import MappingFile from './MappingFile';
import useAvailableFields from '../hooks/useAvailableFilters';
import useSubmit from '../hooks/useSubmit';
import useMappingNames from '../hooks/useMappingNames';

const styles = {
  fieldsHeader: {
    alignItems: 'start',
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    width: '100%'
  },
  fieldsInfoButton: {
    textWrap: 'nowrap'
  },
  headerNote: {
    color: 'text.secondary',
    m: '0 0 0 16px'
  }
};

const MappingForm = () => {
  // State that keeps track of the fields already selected, so those options can be disabled in the other dropdowns
  const [selections, setSelections] = React.useState({});

  // Manages state for opening/closing the field list dialog
  const [openFieldListDialog, setOpenFieldListDialog] = React.useState(false);

  // State containing all the available SECCON fields that can be mapped
  const availableFields = useAvailableFields();

  // All the excel headers that needs to be mapped
  const [headerMap, setHeaderMap] = React.useState(null);

  // Retrieve the existing mapping names
  const mappingNames = useMappingNames();

  // Form submission callback function
  const { handleSubmit, isSubmitting } = useSubmit(headerMap);

  // Initialize the form and retrieve its state
  const form = useForm({
    defaultValues: {
      syncOnSsn: false
    },
    mode: 'onChange',
    shouldUnregister: true
  });
  const { isDirty, isValid } = form.formState;

  /**
   * Handles cancel events by redirecting the user back to the personnel list page
   */
  function handleCancel () {
    // If the user selected a facility, send them back to the facility page
    const facilityId = (new URLSearchParams(window.location.search)).get('facilityId') ?? '';

    window.location.href = `/personnel/list?facilityId=${ facilityId }`;
  }

  /**
   * Handle's field selections by updating the selection state
   * @param oldValue {string} - the field's previous selection value
   * @param newValue {string} - the new selection
   */
  function handleSelection (oldValue, newValue) {
    const newSelections = { ...selections };

    // Remove the old selection value, if its not empty
    if (oldValue) {
      delete newSelections[oldValue.value];
    }

    // Add the new value, if its not empty
    if (newValue !== '') {
      newSelections[newValue.value] = true;
    }

    setSelections(newSelections);
  }

  // Determine if the form has a valid mapping file value or not
  const { errors } = form.formState;
  const mappingFile = form.getValues('mappingFile') ?? [];
  const hasValidMappingFile = !Object.prototype.hasOwnProperty.call(errors, 'mappingFile') && mappingFile.length > 0;

  // If the available fields, list of mapping definition names, or header map is still empty, set loading to true
  const isLoading = availableFields === null || mappingNames === null || headerMap === null;

  // Disable the submit button if the form is loading, pristine, invalid, or currently submitting
  const disableSubmit = isLoading || !isDirty || !isValid || isSubmitting;

  return (
    <>
      <Stack spacing={3}>
        <FormProvider {...form}>
          <ErrorBoundary>
            <NameField mappingNames={mappingNames} />
          </ErrorBoundary>

          <ErrorBoundary>
            <SsnSyncField />
          </ErrorBoundary>

          <ErrorBoundary>
            <MappingFile setHeaderMap={setHeaderMap} />
          </ErrorBoundary>

          { /* Display the loading overlay if a valid mapping file is entered and the form is still loading */ }
          {hasValidMappingFile && isLoading && (
            <>
              <Skeleton animation='wave' variant='rectangular' height={56} />
              <Skeleton animation='wave' variant='rectangular' height={56} />
              <Skeleton animation='wave' variant='rectangular' height={56} />
              <Skeleton animation='wave' variant='rectangular' height={56} />
            </>
          )}

          { /* Display the field list if a valid mapping file was entered and the form is NOT still loading */ }
          {hasValidMappingFile && !isLoading && (
            <>
              <Box sx={styles.fieldsHeader}>
                <Typography variant='h6'>
                  Select a Security Control field to map to each personnel column header:
                  <Typography component='span' display='block' sx={styles.headerNote} variant='subtitle2'>
                    *Note: Social Security Number field (ssn) must be mapped.
                  </Typography>
                </Typography>
                <Button
                  onClick={() => setOpenFieldListDialog(true)}
                  sx={styles.fieldsInfoButton}
                  variant='text'
                >
                  View Personnel Field List
                </Button>
              </Box>
              {Object.keys(headerMap).map((id) => (
                <ErrorBoundary key={id}>
                  <MappingField
                    availableFields={availableFields}
                    header={headerMap[id]}
                    id={id}
                    selections={selections}
                    onSelection={(oldValue, newValue) => handleSelection(oldValue, newValue)}
                  />
                </ErrorBoundary>
              ))}
            </>
          )}
        </FormProvider>
      </Stack>
      <ErrorBoundary>
        <FormFooter
          disabled={disableSubmit}
          isSubmitting={isSubmitting}
          onCancel={() => handleCancel()}
          onSubmit={form.handleSubmit(handleSubmit)}
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <FieldListModal open={openFieldListDialog} onClose={() => setOpenFieldListDialog(false)} />
      </ErrorBoundary>
    </>
  );
};

export default MappingForm;
